import { PAYMENT_TYPES } from '@koala/sdk';
import { type AnyAction } from 'redux';
import orderStatusActions from '../orderStatus/actions';
import actions from './actions';
import { type IPaymentState } from '@/types/paymentInfo';

export const initialState: IPaymentState = {
  storedValueError: '',
  storedValue: null,
  paymentType: null,
  toast: null,
  selectedDigitalWallet: '',
  digitalWalletWarning: false,
  toastPaymentConfirmed: false,
  toastInitFailure: null,
};

export const payment = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case actions.BILLING_ACCOUNT_SET:
      return Object.assign({}, state, {
        paymentType: {
          type: PAYMENT_TYPES.BILLING_ACCOUNT,
          saved_account_id: action.savedAccountId,
        },
      });
    case actions.CREDIT_CARD_SET:
      return Object.assign({}, state, {
        paymentType: {
          ...action.creditCard,
        },
      });
    case actions.PAY_IN_STORE_SET:
      return Object.assign({}, state, {
        paymentType: {
          type: PAYMENT_TYPES.CASH,
        },
      });
    case actions.DIGITAL_WALLET_TOGGLE:
      return Object.assign({}, state, {
        paymentType: {
          type: PAYMENT_TYPES.DIGITAL_WALLET,
          selectedDigitalWallet: action.selectedDigitalWallet,
        },
      });
    case actions.DIGITAL_WALLET_WARNING_SET:
      return Object.assign({}, initialState, {
        digitalWalletWarning: action.hasWarning,
      });
    case actions.PAYMENT_TYPE_RESET:
      return Object.assign({}, state, {
        paymentType: initialState.paymentType,
        toast: {
          ...state.toast,
          created: false,
          error: '',
        },
        toastPaymentConfirmed: false,
      });
    case actions.STORED_VALUE_SET:
      return Object.assign({}, state, {
        storedValue: action.storedValue,
        storedValueError: initialState.storedValueError,
      });
    case actions.STORED_VALUE_ERROR:
      return Object.assign({}, state, {
        storedValue: initialState.storedValue,
        storedValueError: action.message,
      });
    case actions.STORED_VALUE_CLEAR:
    case orderStatusActions.STORED_VALUE_POST:
      return Object.assign({}, state, {
        storedValue: initialState.storedValue,
        storedValueError: initialState.storedValueError,
      });
    case orderStatusActions.ORDER_SUBMISSION_ERROR:
    case orderStatusActions.ORDER_SUBMISSION_PAGE_ERROR:
    case orderStatusActions.ORDER_SUBMISSION_PCI_ERROR:
    case orderStatusActions.ORDER_SUBMISSION_PCI_FAILURE:
    case orderStatusActions.RECOVERY_502_TRIGGERED:
    case orderStatusActions.RESTAURANT_UNAVAILABLE:
    case orderStatusActions.PRICING_UNAVAILABLE:
    case orderStatusActions.BASKET_NOT_FOUND:
    case orderStatusActions.TOAST_PAYMENT_RECOVERY:
    case orderStatusActions.ORDER_PENDING:
      return Object.assign({}, state, {
        toastPaymentConfirmed: false,
      });
    case orderStatusActions.INITIALIZE_ORDER:
    case orderStatusActions.DESTROY_ORDER:
      return initialState;
    case actions.TOAST_PAYMENT_INITIALIZE: {
      // TEAM-15073: a late script load heals itself. Only a successful init clears the
      // failure — `toastPaymentInitialize(false)` is also how teardown and reinitialise
      // start, and clearing there would drop the failure the diner is still reading.
      const healed = action.initialized && state.toastInitFailure;
      return Object.assign({}, state, {
        toast: {
          ...state.toast,
          initialized: action.initialized,
          // The copy has to come down with the failure that raised it. `'unavailable'` writes
          // `providerUnavailable` here, and Toast's monitor callback does not fire for the
          // initial iframe state — so without this a working payment form would sit under a
          // red "this restaurant can't take online payments" until the diner typed into it.
          ...(healed ? { error: '' } : {}),
        },
        toastInitFailure: action.initialized ? null : state.toastInitFailure,
      });
    }
    case actions.TOAST_PAYMENT_INIT_FAILED:
      return Object.assign({}, state, {
        toastInitFailure: action.reason,
      });
    case actions.TOAST_PAYMENT_ERROR:
      return Object.assign({}, state, {
        toast: { ...state.toast, error: action.message },
        toastPaymentConfirmed: false,
      });
    case actions.TOAST_PAYMENT_CREATE:
      return Object.assign({}, state, {
        paymentType: {
          ...state.paymentType,
          card_token: action.paymentIntentId,
          type: state.selectedDigitalWallet
            ? PAYMENT_TYPES.DIGITAL_WALLET
            : PAYMENT_TYPES.CREDIT_CARD_TOKEN,
          expiry_month: parseInt(action.card.expiry.month, 10),
          expiry_year: parseInt(action.card.expiry.year, 10),
          card_brand: action.card.brand,
          last_four: action.card.lastFour,
        },
        toast: {
          ...state.toast,
          error: '',
          created: true,
        },
      });
    case actions.TOAST_PAYMENT_VALIDATE:
      return Object.assign({}, state, {
        selectedDigitalWallet:
          action.selectedDigitalWallet === undefined
            ? state.selectedDigitalWallet
            : action.selectedDigitalWallet,
        toast: {
          ...state.toast,
          isValid: action.isValid,
          error: action.isValid ? '' : state.toast?.error,
        },
      });
    case actions.TOAST_PAYMENT_CONFIRMED:
      return Object.assign({}, state, {
        toastPaymentConfirmed: true,
      });
    default:
      return state;
  }
};
